// Classify shared content (mobile share sheet) into a bookmark or a note.
//
// Rules:
//   - explicit highlight (iOS selection / Android "including link:" marker)
//       -> note WITH a source line
//   - text with exactly 1 URL  -> bookmark (URL-only; scraper fills the title)
//   - text with 2+ URLs        -> note (URLs preserved + auto-linkified)
//   - plain text (0 URLs)      -> note
//
// "text + 1 URL -> bookmark" intentionally wins over "looks like a highlight":
// a marker-less browser highlight is byte-identical to an app entry share
// (e.g. LuxuryEstate), so only an explicit highlight signal yields a note.

import { URL_RE, NOTE_TITLE_MAX, textToNoteHtml, hasTextFragment, textFromFragment } from '@/utils/note-content'

// Chrome (Android) appends "including link: <url>" to shared text selections.
// This is the definitive "this is a page highlight" marker.
export const INCLUDING_LINK_RE = /\bincluding link:?\s*(https?:\/\/\S+)/i

// Strip one pair of wrapping quotes (straight or curly) that browsers add around
// a shared selection, e.g. Chrome's `"Stop hiring"`.
function stripWrappingQuotes(text) {
  const t = (text || '').trim()
  const m = t.match(/^["'“‘]([\s\S]*)["'”’]$/)
  return m ? m[1].trim() : t
}

export function findUrls(text) {
  if (!text) return []
  return text.match(URL_RE) || []
}

// A page-title field (Android EXTRA_SUBJECT / iOS suggestedName) is only usable
// as a bookmark title when it's a real distinct title — not the text blob itself
// and not just a URL. Otherwise return null so the scraper supplies the title.
function usablePageTitle(pageTitle, text) {
  if (!pageTitle) return null
  const t = pageTitle.trim()
  if (!t) return null
  if (t === (text || '').trim()) return null
  if (findUrls(t).length) return null
  return t.slice(0, NOTE_TITLE_MAX)
}

// Chrome prepends "Including link:" to the share subject of a highlight, which
// otherwise leaks into the visible source-link label. Strip it (and reject a
// "title" that is really just a URL) so the label is a clean title or nothing.
function cleanSourceTitle(title, sourceUrl) {
  const t = (title || '').replace(/^\s*including link:?\s*/i, '').trim()
  if (!t) return null
  if (findUrls(t).length) return null
  if (sourceUrl && t === sourceUrl.trim()) return null
  return t.slice(0, NOTE_TITLE_MAX)
}

// Drop a #:~:text= text fragment so a URL shown as the source label reads cleanly
// (the href keeps the fragment so the link still re-scrolls to the highlight).
function stripTextFragment(url) {
  return (url || '').replace(/#:~:text=.*$/i, '')
}

function noteWithSource(body, sourceUrl, sourceTitle) {
  const cleanTitle = cleanSourceTitle(sourceTitle, sourceUrl)
  // Label: a real title when we have one, else the URL without its text fragment.
  const label = cleanTitle || stripTextFragment(sourceUrl)
  const title = (body || cleanTitle || stripTextFragment(sourceUrl) || '').trim().slice(0, NOTE_TITLE_MAX)
  return {
    kind: 'note',
    title,
    contentHtml: textToNoteHtml(body, { sourceUrl, sourceTitle: label }),
    sourceUrl,
    sourceTitle: cleanTitle,
  }
}

// @param {object} input
//   text         primary shared text blob (may contain URLs)
//   pageTitle    distinct page-title field, if any
//   explicitSource {url, title} when the platform already knows it's a highlight
// @returns { kind:'bookmark', url, title } | { kind:'note', title, contentHtml }
export function classifyShare({ text = '', pageTitle = null, explicitSource = null } = {}) {
  const trimmed = (text || '').trim()

  // 1. Explicit highlight (iOS selection): note + source.
  if (explicitSource && explicitSource.url) {
    return noteWithSource(trimmed, explicitSource.url, explicitSource.title || pageTitle)
  }

  // 2. Android Chrome "including link:" marker: strip it from the body, keep as source.
  const marker = trimmed.match(INCLUDING_LINK_RE)
  if (marker) {
    const sourceUrl = marker[1]
    const body = trimmed.replace(INCLUDING_LINK_RE, '').trim()
    return noteWithSource(body, sourceUrl, pageTitle)
  }

  // 3. Browser highlight via text fragment. A single URL carrying a #:~:text=
  //    fragment is a definitive page-highlight marker (Chrome "Share highlight",
  //    Safari, etc.) — unlike a marker-less share, so it doesn't collide with the
  //    "text + 1 URL -> bookmark" app-entry case. Keep the selection as a note and
  //    the page as the source. Body = surrounding quote, or the fragment's text.
  const urls = findUrls(trimmed)
  const fragmentUrls = urls.filter(hasTextFragment)
  if (fragmentUrls.length === 1) {
    const sourceUrl = fragmentUrls[0]
    // Drop the whole line carrying the link, not just the URL token: Chrome puts
    // the URL (and any localized "including link:" / "einschließlich Link:"
    // connector) on its own line, so removing the line strips the connector in
    // every UI language. Fall back to the fragment's own decoded text.
    let body = stripWrappingQuotes(
      trimmed
        .split('\n')
        .filter(line => !hasTextFragment(line))
        .join('\n')
        .trim(),
    )
    if (!body) body = textFromFragment(sourceUrl)
    return noteWithSource(body, sourceUrl, pageTitle)
  }

  // 4. URL count.
  if (urls.length === 1) {
    return {
      kind: 'bookmark',
      url: urls[0],
      title: usablePageTitle(pageTitle, trimmed),
    }
  }

  // 0 or 2+ URLs -> note (no source).
  return {
    kind: 'note',
    title: trimmed.slice(0, NOTE_TITLE_MAX),
    contentHtml: textToNoteHtml(trimmed),
    sourceUrl: null,
    sourceTitle: null,
  }
}
